import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight, ChevronRight, ChevronDown, Clock, Scale, Banknote, MessageSquare, Shield
} from 'lucide-react';
import useSeo from '../hooks/useSeo';
import { SITE, GAMES, formatMad } from '../data/servers';
import { PAGES, gamePath, serverPath, buyPath, sellPath, gameLabel } from './routes';

// ---------- SEO ----------

const breadcrumbJsonLd = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: item.label,
    item: `${SITE.url}${item.to}`,
  })),
});

// Title, description, canonical + fil d'Ariane en JSON-LD
export function usePageSeo({ title, description, path, breadcrumbs, jsonLd = [], noindex = false }) {
  useSeo({
    title,
    description,
    path,
    noindex,
    jsonLd: [...(breadcrumbs ? [breadcrumbJsonLd(breadcrumbs)] : []), ...jsonLd],
  });
}

// ---------- Mise en page ----------

export const Breadcrumbs = ({ items }) => (
  <nav aria-label="Fil d'Ariane" className="max-w-7xl mx-auto px-4 sm:px-6 pt-3 sm:pt-5">
    <ol className="flex flex-wrap items-center gap-1 text-xs sm:text-sm text-gray-500">
      {items.map((item, i) => (
        <li key={item.to} className="flex items-center gap-1">
          {i > 0 && <ChevronRight size={14} className="flex-shrink-0" />}
          {i === items.length - 1
            ? <span aria-current="page" className="text-gray-300">{item.label}</span>
            : <Link to={item.to} className="hover:text-yellow-400">{item.label}</Link>}
        </li>
      ))}
    </ol>
  </nav>
);

export const PageHeader = ({ kicker, title, intro, children }) => (
  <header className="max-w-7xl mx-auto px-4 sm:px-6 pt-4 pb-6 sm:pt-6 sm:pb-10">
    {kicker && <p className="text-yellow-400 text-xs sm:text-sm font-semibold uppercase tracking-wide mb-2">{kicker}</p>}
    <h1 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-3">{title}</h1>
    {intro && <p className="text-sm sm:text-lg text-gray-400 max-w-3xl leading-relaxed">{intro}</p>}
    {children}
  </header>
);

export const Section = ({ id, title, intro, children, className = '' }) => (
  <section id={id} aria-labelledby={id ? `${id}-title` : undefined} className={`py-8 md:py-12 ${className}`}>
    <div className="max-w-7xl mx-auto px-4 sm:px-6">
      {title && <h2 id={id ? `${id}-title` : undefined} className="text-xl sm:text-2xl lg:text-3xl font-bold text-white mb-3 leading-tight">{title}</h2>}
      {intro && <p className="text-sm sm:text-base text-gray-400 max-w-3xl mb-5 md:mb-7 leading-relaxed">{intro}</p>}
      {children}
    </div>
  </section>
);

export const ActionButtons = ({ buyTo = PAGES.buy, sellTo = PAGES.sell, buyLabel = 'Acheter des Kamas', sellLabel = 'Vendre des Kamas', className = '' }) => (
  <div className={`grid grid-cols-2 sm:flex gap-3 ${className}`}>
    <Link to={buyTo} className="px-3 sm:px-6 py-3 text-sm sm:text-base bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-400 hover:to-yellow-500 text-black font-bold rounded-xl flex items-center justify-center gap-2 text-center">
      {buyLabel}
    </Link>
    <Link to={sellTo} className="px-3 sm:px-6 py-3 text-sm sm:text-base bg-gray-800 hover:bg-gray-700 text-white font-bold rounded-xl border border-gray-700 flex items-center justify-center gap-2 text-center">
      {sellLabel}
    </Link>
  </div>
);

export const TextLink = ({ to, children }) => (
  <Link to={to} className="text-yellow-400 hover:underline">{children}</Link>
);

export const ArrowLink = ({ to, children }) => (
  <Link to={to} className="inline-flex items-center gap-1.5 text-yellow-400 font-semibold hover:underline text-sm sm:text-base">
    {children} <ArrowRight size={16} />
  </Link>
);

// Cartes de navigation interne vers d'autres pages
export const LinkCards = ({ links, columns = 'sm:grid-cols-2 lg:grid-cols-3' }) => (
  <ul className={`grid ${columns} gap-3`}>
    {links.map(link => (
      <li key={link.to}>
        <Link to={link.to} className="group h-full flex flex-col bg-gray-900/70 border border-gray-800 hover:border-yellow-500/50 rounded-xl p-4 transition-colors">
          <span className="flex items-center justify-between gap-2 font-semibold text-white group-hover:text-yellow-400">
            {link.title} <ArrowRight size={16} className="flex-shrink-0" />
          </span>
          {link.text && <span className="text-xs sm:text-sm text-gray-400 mt-1 leading-relaxed">{link.text}</span>}
        </Link>
      </li>
    ))}
  </ul>
);

export const Steps = ({ steps }) => (
  <ol className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
    {steps.map((step, i) => (
      <li key={step.title} className="bg-gray-900/70 border border-gray-800 rounded-xl p-3 sm:p-4 flex lg:block gap-3">
        <span className="w-7 h-7 sm:w-8 sm:h-8 flex-shrink-0 rounded-full bg-yellow-500 text-black text-sm font-bold flex items-center justify-center lg:mb-3">{i + 1}</span>
        <div>
          <h3 className="font-semibold text-white text-sm sm:text-base mb-0.5">{step.title}</h3>
          <p className="text-xs sm:text-sm text-gray-400 leading-relaxed">{step.text}</p>
        </div>
      </li>
    ))}
  </ol>
);

export const FaqList = ({ items }) => (
  <div className="grid lg:grid-cols-2 gap-2 sm:gap-3 items-start">
    {items.map(item => (
      <details key={item.q} className="group bg-gray-900/70 border border-gray-800 rounded-xl px-4 py-3 open:border-yellow-500/40">
        <summary className="flex items-center justify-between cursor-pointer list-none font-semibold text-white">
          <h3 className="text-sm sm:text-base">{item.q}</h3>
          <ChevronDown size={18} className="text-gray-500 group-open:rotate-180 transition-transform flex-shrink-0 ml-3" />
        </summary>
        <p className="text-sm text-gray-400 mt-2 leading-relaxed">{item.a}</p>
      </details>
    ))}
  </div>
);

export const CtaBand = ({ title = 'Prêt pour votre achat ou votre vente de Kamas ?', text = 'Choisissez votre serveur Dofus, payez ou soyez payé en dirhams, suivez tout dans le chat.', buyTo, sellTo }) => (
  <section className="py-10 md:py-14 bg-gradient-to-r from-yellow-500/10 via-gray-950 to-yellow-500/10">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
      <h2 className="text-xl sm:text-3xl font-bold text-white mb-2 sm:mb-3 leading-tight">{title}</h2>
      <p className="text-sm sm:text-base text-gray-400 mb-5 sm:mb-7">{text}</p>
      <ActionButtons buyTo={buyTo} sellTo={sellTo} className="sm:justify-center" />
    </div>
  </section>
);

export const TrustGrid = () => (
  <ul className="grid grid-cols-2 lg:grid-cols-4 gap-3">
    {[
      { icon: Scale, title: 'Prix affichés à l\'avance', text: 'Le total en DH est connu avant de valider la commande.' },
      { icon: Banknote, title: 'Paiement local', text: 'Virement bancaire marocain, en dirhams, à l\'achat comme à la vente.' },
      { icon: MessageSquare, title: 'Commande suivie', text: 'Conversation dédiée, statut en direct et preuves de paiement partagées.' },
      { icon: Shield, title: 'Compte protégé', text: 'Mot de passe chiffré et réinitialisation sécurisée par email.' },
    ].map(({ icon: Icon, title, text }) => (
      <li key={title} className="bg-gray-900/60 border border-gray-800 rounded-xl p-3 sm:p-5">
        <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-400 mb-2" />
        <h3 className="text-sm sm:text-base font-semibold text-white mb-1 leading-snug">{title}</h3>
        <p className="text-gray-400 text-xs sm:text-sm">{text}</p>
      </li>
    ))}
  </ul>
);

// ---------- Prix ----------

export const PriceTicker = ({ servers }) => (
  <div className="bg-black/40 border-b border-yellow-500/20 py-2 overflow-hidden" aria-hidden="true">
    <motion.div
      className="flex gap-10 whitespace-nowrap"
      animate={{ x: ['0%', '-50%'] }}
      transition={{ repeat: Infinity, duration: 60, ease: 'linear' }}
    >
      {[...servers, ...servers].map((s, i) => (
        <span key={i} className="flex items-center gap-2 text-sm">
          <span className="text-yellow-400 font-bold">{s.name}</span>
          <span className="text-green-400">Vous achetez {formatMad(s.buy)}</span>
          <span className="text-gray-500">·</span>
          <span className="text-red-400">Vous vendez {formatMad(s.sell)}</span>
        </span>
      ))}
    </motion.div>
  </div>
);

export const UpdatedNote = ({ updatedAt }) => (
  <p className="text-gray-500 text-xs sm:text-sm mt-4 flex items-start gap-2">
    <Clock size={14} className="flex-shrink-0 mt-0.5" />
    <span>
      {updatedAt
        ? `Prix mis à jour le ${new Date(updatedAt).toLocaleString('fr-FR', { dateStyle: 'long', timeStyle: 'short' })}.`
        : 'Prix indicatifs, actualisés automatiquement.'}
      {' '}Cliquez sur un prix pour commander directement sur ce serveur.
    </span>
  </p>
);

export const GamePriceTable = ({ game, servers }) => (
  <div className="bg-gray-900/80 border border-gray-800 rounded-2xl overflow-hidden">
    <table className="w-full text-sm">
      <caption className="text-left px-4 py-3 text-base sm:text-lg font-bold text-white border-b border-gray-800">
        <Link to={gamePath(game)} className="hover:text-yellow-400">Prix Kamas {gameLabel(game)}</Link>
      </caption>
      <thead className="text-gray-500 text-[11px] sm:text-xs uppercase">
        <tr>
          <th scope="col" className="text-left px-4 py-2">Serveur</th>
          <th scope="col" className="text-right px-2 py-2">Vous achetez</th>
          <th scope="col" className="text-right px-4 py-2">Vous vendez</th>
        </tr>
      </thead>
      <tbody>
        {servers.map(s => (
          <tr key={s.slug} className="border-t border-gray-800/70 hover:bg-gray-800/40">
            <th scope="row" className="text-left px-4 py-2 font-medium">
              <Link to={serverPath(s)} className="text-white hover:text-yellow-400">{s.name}</Link>
            </th>
            <td className="text-right px-2 py-2 whitespace-nowrap">
              <Link to={buyPath(s)} className="text-green-400 hover:underline font-semibold" title={`Acheter des Kamas ${s.name}`}>{formatMad(s.buy)}</Link>
            </td>
            <td className="text-right px-4 py-2 whitespace-nowrap">
              <Link to={sellPath(s)} className="text-red-400 hover:underline font-semibold" title={`Vendre des Kamas ${s.name}`}>{formatMad(s.sell)}</Link>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const byGame = (servers) => GAMES.map(game => ({ game, servers: servers.filter(s => s.game === game) }));

// Dofus 3 à gauche ; Rétro + Touch empilés sur tablette, en colonnes sur desktop
export const AllPriceTables = ({ servers }) => {
  const [dofus3, ...others] = byGame(servers);
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6 items-start">
      <GamePriceTable {...dofus3} />
      <div className="space-y-4 lg:space-y-0 lg:contents">
        {others.map(group => <GamePriceTable key={group.game} {...group} />)}
      </div>
    </div>
  );
};

export const ServerCard = ({ server: s }) => (
  <article className="bg-gray-900/80 border border-gray-800 hover:border-yellow-500/40 rounded-xl p-3 sm:p-4 flex flex-col transition-colors">
    <div className="flex items-center justify-between gap-2 mb-1">
      <h3 className="text-base font-bold">
        <Link to={serverPath(s)} className="text-white hover:text-yellow-400">Kamas {s.name}</Link>
      </h3>
      <span className="text-[11px] px-2 py-0.5 rounded-full bg-yellow-500/10 text-yellow-400 border border-yellow-500/20 flex-shrink-0">{s.type}</span>
    </div>
    <p className="text-xs sm:text-sm text-gray-400 leading-relaxed mb-2 flex-1">{s.description}</p>
    <dl className="grid grid-cols-2 gap-2 mb-2 text-xs sm:text-sm">
      <div className="bg-green-500/10 border border-green-500/30 rounded-lg px-2 py-1 flex items-baseline justify-between gap-1">
        <dt className="text-green-400">Vous achetez</dt>
        <dd className="font-bold text-white whitespace-nowrap">{formatMad(s.buy)}</dd>
      </div>
      <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-2 py-1 flex items-baseline justify-between gap-1">
        <dt className="text-red-400">Vous vendez</dt>
        <dd className="font-bold text-white whitespace-nowrap">{formatMad(s.sell)}</dd>
      </div>
    </dl>
    <div className="grid grid-cols-2 gap-2 text-xs sm:text-sm font-semibold">
      <Link to={buyPath(s)} className="py-1.5 px-1 text-center bg-yellow-500 hover:bg-yellow-400 text-black rounded-lg">Acheter Kamas {s.name}</Link>
      <Link to={sellPath(s)} className="py-1.5 px-1 text-center bg-gray-800 hover:bg-gray-700 text-white rounded-lg">Vendre Kamas {s.name}</Link>
    </div>
  </article>
);

export const ServerGrid = ({ servers }) => (
  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4">
    {servers.map(s => <ServerCard key={s.slug} server={s} />)}
  </div>
);

export const ComparisonTable = ({ servers, updatedAt }) => {
  const rows = servers.filter(s => s.competitor);
  return (
    <>
      {rows.length === 0 ? (
        <p className="text-gray-400 text-sm sm:text-base">
          Nos prix sont toujours sous ceux de TryAndJudge pour l'achat et au-dessus pour la vente, jusqu'à 1 DH par million de Kamas.
          Le détail par serveur s'affiche dès que les prix du jour sont chargés.
        </p>
      ) : (
        <>
          <div className="overflow-x-auto bg-gray-900/80 border border-gray-800 rounded-2xl">
            <table className="w-full text-xs sm:text-sm min-w-[520px]">
              <caption className="sr-only">Comparatif des prix Kamas entre AV Kamas et TryAndJudge</caption>
              <thead className="text-gray-500 text-[11px] sm:text-xs uppercase">
                <tr className="border-b border-gray-800">
                  <th scope="col" className="sticky left-0 bg-gray-900 text-left px-3 sm:px-5 py-2">Serveur</th>
                  <th scope="col" className="text-right px-2 sm:px-3 py-2">Achat TryAndJudge</th>
                  <th scope="col" className="text-right px-2 sm:px-3 py-2 text-green-400">Achat AV Kamas</th>
                  <th scope="col" className="text-right px-2 sm:px-3 py-2">Vente TryAndJudge</th>
                  <th scope="col" className="text-right px-3 sm:px-5 py-2 text-green-400">Vente AV Kamas</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(s => (
                  <tr key={s.slug} className="border-t border-gray-800/70">
                    <th scope="row" className="sticky left-0 bg-gray-900 text-left px-3 sm:px-5 py-1.5 font-medium">
                      <Link to={serverPath(s)} className="text-white hover:text-yellow-400">{s.name}</Link>{' '}
                      <span className="block sm:inline whitespace-nowrap text-[11px] sm:text-sm text-gray-500 font-normal"><span className="hidden sm:inline">· </span>{gameLabel(s.game)}</span>
                    </th>
                    <td className="text-right px-2 sm:px-3 py-1.5 text-gray-400 whitespace-nowrap">{formatMad(s.competitor.buy)}</td>
                    <td className="text-right px-2 sm:px-3 py-1.5 text-green-400 font-semibold whitespace-nowrap">{formatMad(s.buy)}</td>
                    <td className="text-right px-2 sm:px-3 py-1.5 text-gray-400 whitespace-nowrap">{formatMad(s.competitor.sell)}</td>
                    <td className="text-right px-3 sm:px-5 py-1.5 text-green-400 font-semibold whitespace-nowrap">{formatMad(s.sell)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {rows.length > 1 && <p className="sm:hidden text-gray-500 text-[11px] mt-1">← Faites glisser le tableau pour voir toutes les colonnes →</p>}
        </>
      )}
      <p className="text-gray-500 text-[11px] sm:text-xs mt-3 leading-relaxed">
        Prix TryAndJudge relevés automatiquement sur leurs pages publiques
        {updatedAt ? ` le ${new Date(updatedAt).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}` : ''}.
        « Achat » : prix payé par le client pour 1M de Kamas. « Vente » : prix versé au client pour 1M de Kamas.
        TryAndJudge est une marque de son propriétaire, sans lien avec AV Kamas.
      </p>
    </>
  );
};
